import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PatientContactTypeEnum } from '@ffd/api-client';
import { HealthHistoryDialogService } from '@libs/home';
import { formatPhoneNumber } from '@libs/utils';
import { ContactData, HealthHistoryContactRowComponent } from '../../../health-history-contact-row/health-history-contact-row.component';

export interface ProfileContact {
  contactType: PatientContactTypeEnum;
  contactName?: string;
  phoneNumber?: string;
}
export const CONTACT_NAME_CHAR_LIMIT = 29;
export const CONTACT_PHONE_CHAR_LIMIT = 10;

@Component({
  selector: 'lib-health-history-patient-secondary-contacts',
  imports: [CommonModule, SvgIconComponent, TranslateModule, ReactiveFormsModule, HealthHistoryContactRowComponent],
  templateUrl: './health-history-patient-secondary-contacts.component.html',
  styleUrl: './health-history-patient-secondary-contacts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthHistoryPatientSecondaryContactsComponent {
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);

  public isEditMode = signal(false);
  public contactForm = new FormGroup({
    emergency: new FormControl<ContactData | null>(null, [this.contactValidator()]),
    pharmacy: new FormControl<ContactData | null>(null, [this.contactValidator(true)]),
    physician: new FormControl<ContactData | null>(null, [this.contactValidator()]),
  });

  protected readonly patient = computed(() => this.healthHistoryDialogService.viewModel().patient);

  private contactValidator(isPharmacy = false): ValidatorFn {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as ContactData | null;
      if (!value) return null;

      const errors: ValidationErrors = {};
      const nameValue = value.name?.trim() || '';
      const phoneValue = value.phone?.trim() || '';
      const isBothEmpty = !nameValue && !phoneValue;
      const isBothFilled = !!nameValue && !!phoneValue;
      const isValidPharmacy = isPharmacy && nameValue && !phoneValue;

      if (nameValue.length > CONTACT_NAME_CHAR_LIMIT) {
        errors['nameTooLong'] = true;
      }

      const trimmedPhoneValue = phoneValue.replaceAll(/\D/g, ''); // Remove non-digit characters for length check
      if (phoneValue && trimmedPhoneValue.length < CONTACT_PHONE_CHAR_LIMIT) {
        errors['phoneRequired'] = true;
      }

      // Both empty or both filled is valid
      // For pharmacy: name only is valid, but phone only is not
      if (!(isBothEmpty || isBothFilled || isValidPharmacy)) {
        // Invalid cases: one field filled, other empty
        // Exception: pharmacy can have name only
        if (nameValue && !phoneValue && !isPharmacy) {
          errors['phoneRequired'] = true;
        }
        if (!nameValue && phoneValue) {
          errors['nameRequired'] = true;
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  constructor() {
    // Update form when patient data changes
    effect(() => {
      const patient = this.patient();
      if (patient?.contacts) {
        this.initializeFormWithPatientData(patient.contacts);
      }
    });
  }

  public saveContactDetails(): void {
    if (this.contactForm.valid) {
      const contactUpdates = this.buildContactUpdatesFromForm();
      this.healthHistoryDialogService.updateMultiplePatientContacts(contactUpdates);
      this.isEditMode.set(false);
    }
  }

  public cancel(): void {
    const patient = this.patient();
    if (patient?.contacts) {
      this.initializeFormWithPatientData(patient.contacts);
    } else {
      this.contactForm.reset();
    }
    this.isEditMode.set(false);
  }

  protected onEditClick(): void {
    if (this.isEditMode()) {
      this.contactForm.reset();
    }
    this.isEditMode.update((isEditMode) => !isEditMode);
  }

  private initializeFormWithPatientData(
    contacts: Array<{ contactType?: PatientContactTypeEnum; contactName?: string; phoneNumber?: string }>,
  ): void {
    const emergencyContact = contacts.find((c) => c.contactType === PatientContactTypeEnum.EMERGENCY_CONTACT);
    const pharmacyContact = contacts.find((c) => c.contactType === PatientContactTypeEnum.PREFERRED_PHARMACY);
    const physicianContact = contacts.find((c) => c.contactType === PatientContactTypeEnum.PHYSICIAN_CONTACT);

    this.contactForm.patchValue(
      {
        emergency: emergencyContact
          ? {
              name: emergencyContact.contactName || null,
              phone: formatPhoneNumber(emergencyContact.phoneNumber) || null,
            }
          : null,
        pharmacy: pharmacyContact
          ? {
              name: pharmacyContact.contactName || null,
              phone: formatPhoneNumber(pharmacyContact.phoneNumber) || null,
            }
          : null,
        physician: physicianContact
          ? {
              name: physicianContact.contactName || null,
              phone: formatPhoneNumber(physicianContact.phoneNumber) || null,
            }
          : null,
      },
      { emitEvent: false },
    );
  }

  private buildContactUpdatesFromForm(): Array<ProfileContact> {
    const formValue = this.contactForm.value;
    const contactUpdates: Array<ProfileContact> = [];

    this.addContactIfProvided(contactUpdates, formValue.emergency, PatientContactTypeEnum.EMERGENCY_CONTACT);
    this.addContactIfProvided(contactUpdates, formValue.pharmacy, PatientContactTypeEnum.PREFERRED_PHARMACY);
    this.addContactIfProvided(contactUpdates, formValue.physician, PatientContactTypeEnum.PHYSICIAN_CONTACT);

    return contactUpdates;
  }

  private addContactIfProvided(
    contactUpdates: Array<ProfileContact>,
    contactData: ContactData | null | undefined,
    contactType: PatientContactTypeEnum,
  ): void {
    if (contactData?.name?.trim() || contactData?.phone?.trim()) {
      contactUpdates.push({
        contactType,
        contactName: contactData.name?.trim() || '',
        phoneNumber: this.cleanPhoneNumber(contactData.phone),
      });
    }
  }

  private cleanPhoneNumber(phoneNumber: string | null): string {
    if (!phoneNumber) {
      return '';
    }
    return phoneNumber.replaceAll(/\D/g, '');
  }
}
